import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
	Box,
	Typography,
	IconButton,
	Autocomplete,
	TextField,
	CircularProgress,
	useMediaQuery,
	useTheme
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CalendarTodayIcon from '@mui/icons-material/CalendarMonth'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { supabase } from '../../utils/supabase'
import {
	generateTimeSlots,
	isDateClosed,
	canEmployeePerformService,
	shouldDisableDate
} from '../../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrBefore)

// Calendar Day Cell Component
const CalendarDayCell = ({ day, isCurrentMonth, isSelected, isToday, isAvailable, isPast, onClick }) => {
	if (!day) {
		return <Box sx={{ width: '100%', padding: '16px 8px' }} />
	}

	const disabled = !isCurrentMonth || isPast

	return (
		<Box
			onClick={!disabled && isAvailable ? onClick : undefined}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '16px 8px',
				borderRadius: '4px',
				cursor: disabled || !isAvailable ? 'default' : 'pointer',
				bgcolor: isSelected ? '#9F7AEA' : isAvailable && isCurrentMonth && !isPast ? '#E9D8FD' : disabled ? '#FFFFFF' : '#E4E4E4',
				border: isSelected ? '1px solid #6B6B6B' : 'none',
				opacity: !isCurrentMonth ? 0.3 : 1,
				transition: 'all 0.2s ease',
				flex: 1,
				'&:hover': !disabled && isAvailable ? {
					bgcolor: isSelected ? '#805AD5' : '#D6BCFA'
				} : {}
			}}
		>
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: isSelected ? 600 : 400,
				fontSize: '13px',
				lineHeight: 1.21,
				color: isSelected ? '#6B46C1' : disabled ? '#A0AEC0' : '#2D3748'
			}}>
				{day}
			</Typography>
		</Box>
	)
}

// Time Slot Button Component
const TimeSlotButton = ({ time, isSelected, isAvailable, onClick }) => {
	return (
		<Box
			onClick={isAvailable ? onClick : undefined}
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '10px 0px',
				bgcolor: isSelected ? '#E9D8FD' : '#FFFFFF',
				border: `1px solid ${isSelected ? '#9F7AEA' : '#9F7AEA'}`,
				borderRadius: '12px',
				cursor: isAvailable ? 'pointer' : 'not-allowed',
				opacity: isAvailable ? 1 : 0.5,
				transition: 'all 0.2s ease',
				'&:hover': isAvailable ? {
					bgcolor: isSelected ? '#D6BCFA' : '#F7FAFC'
				} : {}
			}}
		>
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 500,
				fontSize: '13px',
				lineHeight: 1.85,
				color: '#2D3748'
			}}>
				{time}
			</Typography>
		</Box>
	)
}

// Main DateTimeStep Component
function DateTimeStep({
	merchant,
	employees,
	services,
	totalDuration,
	selectedDate,
	onDateChange,
	selectedTime,
	onTimeChange,
	selectedEmployee,
	onEmployeeChange,
	petServices // To know which services are needed
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const [currentMonth, setCurrentMonth] = useState(dayjs())
	const [timeFilter, setTimeFilter] = useState('morning') // 'morning' | 'afternoon'
	const [availableSlots, setAvailableSlots] = useState([])
	const [loadingSlots, setLoadingSlots] = useState(false)
	const [dateAvailability, setDateAvailability] = useState({}) // { 'YYYY-MM-DD': true/false }
	const [isCalendarExpanded, setIsCalendarExpanded] = useState(true) // For mobile collapsed view

	// Get unique service IDs from petServices
	const serviceIds = useMemo(() => {
		const ids = new Set()
		petServices.forEach(ps => {
			if (ps.serviceId) ids.add(ps.serviceId)
		})
		return Array.from(ids)
	}, [petServices])

	// Get employees who can perform all selected services
	const availableEmployees = useMemo(() => {
		return employees.filter(emp => {
			return serviceIds.every(serviceId => canEmployeePerformService(emp, serviceId))
		})
	}, [employees, serviceIds])

	// Employee options for dropdown
	const employeeOptions = useMemo(() => {
		const options = [{ id: null, name: 'Any Professional' }]
		availableEmployees.forEach(emp => {
			options.push({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` })
		})
		return options
	}, [availableEmployees])

	// Get employees to check for availability (either selected or all)
	const employeesToCheck = useMemo(() => {
		if (selectedEmployee) {
			// Filter to only the selected employee
			return availableEmployees.filter(emp => emp.id === selectedEmployee)
		}
		// Return all available employees if "Any Professional" is selected
		return availableEmployees
	}, [availableEmployees, selectedEmployee])

	// Generate calendar days for current month
	const calendarDays = useMemo(() => {
		const startOfMonth = currentMonth.startOf('month')
		const endOfMonth = currentMonth.endOf('month')
		const startDay = startOfMonth.day() // 0 = Sunday
		const daysInMonth = endOfMonth.date()

		const days = []

		// Previous month days
		const prevMonth = currentMonth.subtract(1, 'month')
		const daysInPrevMonth = prevMonth.endOf('month').date()
		for (let i = startDay - 1; i >= 0; i--) {
			days.push({
				day: daysInPrevMonth - i,
				isCurrentMonth: false,
				date: prevMonth.date(daysInPrevMonth - i)
			})
		}

		// Current month days
		for (let i = 1; i <= daysInMonth; i++) {
			const date = currentMonth.date(i)
			days.push({
				day: i,
				isCurrentMonth: true,
				date
			})
		}

		// Next month days (to fill remaining week)
		const remainingInLastWeek = (7 - (days.length % 7)) % 7
		for (let i = 1; i <= remainingInLastWeek; i++) {
			days.push({
				day: i,
				isCurrentMonth: false,
				date: currentMonth.add(1, 'month').date(i)
			})
		}

		return days
	}, [currentMonth])

	// Group calendar days into weeks, only including weeks with current month dates
	const calendarWeeks = useMemo(() => {
		const weeks = []
		for (let i = 0; i < calendarDays.length; i += 7) {
			const week = calendarDays.slice(i, i + 7)
			// Only include weeks that have at least one current month day
			if (week.some(day => day.isCurrentMonth)) {
				weeks.push(week)
			}
		}
		return weeks
	}, [calendarDays])

	// Find the week index containing the selected date
	const selectedWeekIndex = useMemo(() => {
		if (!selectedDate) return -1
		return calendarWeeks.findIndex(week =>
			week.some(day => day.date.isSame(selectedDate, 'day'))
		)
	}, [calendarWeeks, selectedDate])

	// Get weeks to display based on expanded state (mobile only)
	const displayedWeeks = useMemo(() => {
		if (!isMobile || isCalendarExpanded || selectedWeekIndex === -1) {
			return calendarWeeks
		}
		// Show only the selected week
		return [calendarWeeks[selectedWeekIndex]]
	}, [isMobile, isCalendarExpanded, selectedWeekIndex, calendarWeeks])

	// Check availability for dates in current month
	useEffect(() => {
		const checkDateAvailability = async () => {
			if (!merchant || serviceIds.length === 0) return

			const availability = {}
			const today = dayjs()

			for (const dayInfo of calendarDays) {
				if (!dayInfo.isCurrentMonth) continue

				const dateKey = dayInfo.date.format('YYYY-MM-DD')

				// Past dates are unavailable
				if (dayInfo.date.isBefore(today, 'day')) {
					availability[dateKey] = false
					continue
				}

				// Check if date is closed
				if (shouldDisableDate(dayInfo.date, merchant)) {
					availability[dateKey] = false
					continue
				}

				// Check if any employee can perform services
				if (availableEmployees.length === 0) {
					availability[dateKey] = false
					continue
				}

				// Date is potentially available
				availability[dateKey] = true
			}

			setDateAvailability(availability)
		}

		checkDateAvailability()
	}, [calendarDays, merchant, serviceIds, availableEmployees])

	// Load available time slots when date or employee changes
	const loadAvailableSlots = useCallback(async () => {
		if (!selectedDate || !merchant || serviceIds.length === 0) {
			setAvailableSlots([])
			return
		}

		// If a specific employee is selected but not found, no slots available
		if (employeesToCheck.length === 0) {
			setAvailableSlots([])
			return
		}

		setLoadingSlots(true)

		try {
			// Check if date is closed
			if (isDateClosed(selectedDate, merchant.closed_recurring_dates)) {
				setAvailableSlots([])
				return
			}

			// Generate all possible time slots for this day
			const dayOfWeek = selectedDate.day()
			const allSlots = generateTimeSlots(dayOfWeek, merchant.weekly_hours)

			if (allSlots.length === 0) {
				setAvailableSlots([])
				return
			}

			// Fetch existing appointments for the day
			const dayStart = selectedDate.startOf('day').utc()
			const dayEnd = selectedDate.endOf('day').utc()

			const { data: dayAppointments, error } = await supabase
				.from('appointments')
				.select('service_id, employee_id, start_ts, end_ts')
				.eq('merchant_id', merchant.id)
				.in('status', ['booked', 'bookPending'])
				.gte('start_ts', dayStart.toISOString())
				.lte('start_ts', dayEnd.toISOString())

			if (error) {
				console.error('Error loading appointments:', error)
				setAvailableSlots([])
				return
			}

			const now = dayjs()

			// Check availability for each slot
			// A slot is available if at least one employee from employeesToCheck is free
			const available = allSlots.filter(slot => {
				try {
					const localDateTime = selectedDate.format('YYYY-MM-DD') + ' ' + slot
					const utcStart = dayjs.tz(localDateTime, merchant.timezone).utc()
					const utcEnd = utcStart.add(totalDuration, 'minute')

					// Skip past time slots
					if (utcStart.isBefore(now)) {
						return false
					}

					// Find appointments that overlap with this time slot
					// Two ranges overlap if: start1 < end2 AND start2 < end1
					// Back-to-back appointments (end1 == start2) should NOT overlap
					const overlappingAppointments = (dayAppointments || []).filter(appt => {
						const apptStart = dayjs(appt.start_ts)
						const apptEnd = dayjs(appt.end_ts)
						// Overlap: new slot starts before existing ends AND new slot ends after existing starts
						return utcStart.isBefore(apptEnd) && utcEnd.isAfter(apptStart)
					})

					// Check if at least one employee from employeesToCheck is free
					// An employee is free if:
					// 1. They can perform the required service
					// 2. They don't have any overlapping appointments assigned to them
					const freeEmployees = employeesToCheck.filter(emp => {
						// Check if employee can perform the service
						if (!canEmployeePerformService(emp, serviceIds[0])) {
							return false
						}
						// Check if employee has any overlapping appointments
						const hasOverlap = overlappingAppointments.some(appt => appt.employee_id === emp.id)
						return !hasOverlap
					})

					return freeEmployees.length > 0
				} catch (err) {
					console.error('Error checking slot:', slot, err)
					return false
				}
			})

			setAvailableSlots(available)
		} catch (err) {
			console.error('Error loading available slots:', err)
			setAvailableSlots([])
		} finally {
			setLoadingSlots(false)
		}
	}, [selectedDate, merchant, serviceIds, totalDuration, employeesToCheck])

	useEffect(() => {
		loadAvailableSlots()
	}, [loadAvailableSlots])

	// Filter slots by morning/afternoon (slots are already generated on the
	// 15-minute grid by generateTimeSlots)
	const filteredSlots = useMemo(() => {
		return availableSlots.filter(slot => {
			const [hourStr] = slot.split(':')
			const hour = parseInt(hourStr, 10)

			if (timeFilter === 'morning') {
				return hour < 12
			} else {
				return hour >= 12
			}
		})
	}, [availableSlots, timeFilter])

	// Format time for display
	const formatTime = (slot) => {
		return dayjs(`2000-01-01 ${slot}`).format('hh:mm A')
	}

	// Handle month navigation
	const handlePrevMonth = () => {
		setCurrentMonth(prev => prev.subtract(1, 'month'))
	}

	const handleNextMonth = () => {
		setCurrentMonth(prev => prev.add(1, 'month'))
	}

	// Handle date selection
	const handleDateSelect = (date) => {
		onDateChange(date)
		onTimeChange(null) // Reset time when date changes
		// Collapse calendar on mobile after selection
		if (isMobile) {
			setIsCalendarExpanded(false)
		}
	}

	// Handle time selection
	const handleTimeSelect = (slot) => {
		onTimeChange(slot)
	}

	// Handle employee change - reset time since availability may change
	const handleEmployeeChange = (employeeId) => {
		onEmployeeChange(employeeId)
		onTimeChange(null) // Reset time when employee changes
	}

	const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '18px',
			width: '100%'
		}}>
			{/* Section Title */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: isMobile ? '24px' : '28px',
				lineHeight: 1.29,
				color: '#2D3748'
			}}>
				Select a date and time
			</Typography>

			{/* Calendar Navigation */}
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				gap: isMobile ? '8px' : '18px'
			}}>
				<IconButton onClick={handlePrevMonth} size="small">
					<ChevronLeftIcon />
				</IconButton>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: isMobile ? '18px' : '24px',
					lineHeight: 1.5,
					color: '#2D3748'
				}}>
					{currentMonth.format('MMMM YYYY')}
				</Typography>
				<IconButton onClick={handleNextMonth} size="small">
					<ChevronRightIcon />
				</IconButton>

				{/* Calendar expand icon - mobile only, shown when collapsed */}
				{isMobile && !isCalendarExpanded && selectedDate && (
					<IconButton
						onClick={() => setIsCalendarExpanded(true)}
						size="small"
						sx={{
							ml: 'auto',
							// bgcolor: '#E9D8FD',
							borderRadius: '8px',
							'&:hover': { bgcolor: '#D6BCFA' }
						}}
					>
						<CalendarTodayIcon sx={{ color: "black", fontSize: '20px' }} />
					</IconButton>
				)}

				{/* Legend - desktop only */}
				{!isMobile && (
					<Box sx={{ display: 'flex', gap: '18px', ml: 'auto' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Box sx={{ width: '24px', height: '24px', bgcolor: '#E9D8FD', borderRadius: '2px' }} />
							<Typography sx={{ fontFamily: 'Inter', fontSize: '13px', color: '#4A5568' }}>
								Available
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Box sx={{ width: '24px', height: '24px', bgcolor: '#E4E4E4', borderRadius: '2px' }} />
							<Typography sx={{ fontFamily: 'Inter', fontSize: '13px', color: '#4A5568' }}>
								Unavailable
							</Typography>
						</Box>
					</Box>
				)}
			</Box>

			{/* Main Content */}
			<Box sx={{
				display: 'flex',
				flexDirection: isMobile ? 'column' : 'row',
				gap: '18px'
			}}>
				{/* Calendar */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '8px',
					width: isMobile ? '100%' : '370px'
				}}>
					{/* Day Names */}
					<Box sx={{
						display: 'flex',
						gap: '4px'
					}}>
						{dayNames.map(name => (
							<Box
								key={name}
								sx={{
									flex: 1,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									padding: '8px',
									borderRadius: '4px'
								}}
							>
								<Typography sx={{
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '13px',
									lineHeight: 1.21,
									color: '#A0AEC0'
								}}>
									{name}
								</Typography>
							</Box>
						))}
					</Box>

					{/* Calendar Grid */}
					{displayedWeeks.map((week, weekIdx) => (
						<Box key={weekIdx} sx={{ display: 'flex', gap: '4px' }}>
							{week.map((dayInfo, idx) => {
								const dateKey = dayInfo.date.format('YYYY-MM-DD')
								const isAvailable = dateAvailability[dateKey] === true
								const isSelected = selectedDate && selectedDate.isSame(dayInfo.date, 'day')
								const isPast = dayInfo.date.isBefore(dayjs(), 'day')

								return (
									<CalendarDayCell
										key={idx}
										day={dayInfo.day}
										isCurrentMonth={dayInfo.isCurrentMonth}
										isSelected={isSelected}
										isToday={dayInfo.date.isSame(dayjs(), 'day')}
										isAvailable={isAvailable}
										isPast={isPast}
										onClick={() => handleDateSelect(dayInfo.date)}
									/>
								)
							})}
						</Box>
					))}
				</Box>

				{/* Time Selection Panel */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
					padding: '16px',
					bgcolor: '#FFFFFF',
					borderRadius: '12px',
					boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)',
					flex: 1,
					maxHeight: isMobile ? 'none' : '400px',
					overflow: 'auto'
				}}>
					{/* Employee Selector */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 600,
							fontSize: '15px',
							lineHeight: 1.6,
							color: '#2D3748'
						}}>
							Select a professional
						</Typography>
						<Autocomplete
							options={employeeOptions}
							getOptionLabel={(option) => option.name}
							value={employeeOptions.find(e => e.id === selectedEmployee) || employeeOptions[0]}
							onChange={(e, newValue) => handleEmployeeChange(newValue?.id || null)}
							renderInput={(params) => (
								<TextField
									{...params}
									sx={{
										'& .MuiOutlinedInput-root': {
											padding: '4px 8px',
											borderRadius: '12px',
											bgcolor: '#FFFFFF',
											border: '1px solid #A8A7A7',
											'& fieldset': { border: 'none' },
											'&:hover': { borderColor: '#9F7AEA' }
										},
										'& .MuiInputBase-input': {
											fontFamily: 'Inter',
											fontWeight: 500,
											fontSize: '16px',
											lineHeight: 1.85,
											color: 'rgba(0, 0, 0, 0.75)',
											padding: '12px 16px'
										}
									}}
								/>
							)}
							popupIcon={<KeyboardArrowDownIcon sx={{ color: '#A0AEC0' }} />}
						/>
					</Box>

					{/* Morning/Afternoon Filter */}
					<Box sx={{
						display: 'flex',
						gap: '10px'
					}}>
						<Box
							onClick={() => setTimeFilter('morning')}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								padding: '12px 16px',
								alignItems: 'center',
								width: '100px',
								bgcolor: timeFilter === 'morning' ? '#E9D8FD' : '#FFFFFF',
								border: `1px solid ${timeFilter === 'morning' ? '#9F7AEA' : '#C7C7C7'}`,
								borderRadius: '12px',
								cursor: 'pointer',
								transition: 'all 0.2s ease'
							}}
						>
							<Typography sx={{
								fontFamily: 'Inter',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: 1.85,
								color: '#2D3748'
							}}>
								Morning
							</Typography>
						</Box>
						<Box
							onClick={() => setTimeFilter('afternoon')}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								width: '100px',
								padding: '12px 16px',
								alignItems: 'center',
								bgcolor: timeFilter === 'afternoon' ? '#E9D8FD' : '#FFFFFF',
								border: `1px solid ${timeFilter === 'afternoon' ? '#9F7AEA' : '#C7C7C7'}`,
								borderRadius: '12px',
								cursor: 'pointer',
								transition: 'all 0.2s ease'
							}}
						>
							<Typography sx={{
								fontFamily: 'Inter',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: 1.85,
								color: timeFilter === 'afternoon' ? '#2D3748' : 'rgba(45, 55, 72, 0.8)'
							}}>
								Afternoon
							</Typography>
						</Box>
					</Box>

					{/* Divider */}
					<Box sx={{ width: '100%', height: '1px', bgcolor: '#CBD5E0' }} />

					{/* Time Slots */}
					{!selectedDate ? (
						<Typography sx={{
							fontFamily: 'Inter',
							fontSize: '14px',
							color: '#4A5568',
							textAlign: 'center',
							py: 3
						}}>
							Select a date to view available times
						</Typography>
					) : loadingSlots ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={24} sx={{ color: '#9F7AEA' }} />
						</Box>
					) : filteredSlots.length === 0 ? (
						<Typography sx={{
							fontFamily: 'Inter',
							fontSize: '14px',
							color: '#4A5568',
							textAlign: 'center',
							py: 3
						}}>
							No {timeFilter} slots available on this date
						</Typography>
					) : (
						<Box sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
							gap: '10px'
						}}>
							{filteredSlots.map(slot => (
								<TimeSlotButton
									key={slot}
									time={formatTime(slot)}
									isSelected={selectedTime === slot}
									isAvailable={true}
									onClick={() => handleTimeSelect(slot)}
								/>
							))}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	)
}

export default DateTimeStep

